import Head from 'next/head';
import React from 'react';
import PageLayout from '@/components/PageLayout.js';
import getLayoutProps from '@/utils/layout/layout.js';
import { getBrandPageById } from '@/api/cms.js';
import { BrandCatalogue } from '@/components/BrandCatalogue/BrandCatalogue';
import { getCategoriesFromFile } from '@/utils/getCategories';
import { BrandHeroSection, ImageGallery } from '@desquared/wave-components-library';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold.js';
import { useTranslations } from 'next-intl';
import { BrandProductSections } from '@/components/BrandProductSections/BrandProductSections.jsx';
import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { titleCaseString } from '@/utils/stringManipulationUtils.js';

export default function BrandPage(props) {
  const isMobile = useWindowUnderThreshold(850);
  const img = isMobile ? props.brandResponsiveImage : props.brandWebImage;
  const t = useTranslations();
  const images = props.imageGallery?.map(el => {
    return {
      img: el.image,
      title: el.originalName,
    };
  });

  const lightBoxAriaLabels = {
    closeAriaLabel: t('common.asides.side_cart.close'),
    previousSlideAriaLabel: t('common.previous_slide_aria_label'),
    nextSlideAriaLabel: t('common.next_slide_aria_label'),
    carouselAriaLabel: t('common.carousel_aria_label'),
  };

  const genericDescription =
    t('common.brand.brand_description_first_text') +
    ' ' +
    props?.brand +
    ' ' +
    t('common.brand.brand_description_second_text') +
    ' ' +
    props?.brand +
    ' ' +
    t('common.brand.brand_description_third_text') +
    '.';

  return (
    <>
      <Head>
        <title>
          {process.env.displayName} | {titleCaseString(props?.brand)}
        </title>
        <meta name="description" content={props?.brandDescription ? props.brandDescription : genericDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={props.responsiveLogo} />
      </Head>
      <main>
        {img && (
          <BrandHeroSection
            img={img}
            ImageComponent={CustomImage}
            brandTitle={props.brandTitle}
            brandSubtitle={props.brandSubtitle}
            brandDescription={props.brandDescription}
            hasShareButton={process.env.hasShareButton}
            hasFollowButton={process.env.hasFollowButton}
            shareButtonText={t('common.brand.share')}
            followButtonText={t('common.brand.follow')}
          />
        )}

        {images?.length > 0 && (
          <div style={{ marginTop: '2.5rem' }}>
            <ImageGallery images={images.slice(0, 4)} imageGalleryAriaLabels={lightBoxAriaLabels} />
          </div>
        )}

        {props?.brandSections?.length > 0 && <BrandProductSections brandSections={props.brandSections} />}

        <BrandCatalogue brand={props.brand} categoriesFromFile={props.categoriesFromFile}></BrandCatalogue>
      </main>
    </>
  );
}

BrandPage.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  const categoriesFromFile = await getCategoriesFromFile(context.locale);
  const slug = context?.params?.slug?.[0];

  let contentData = null;
  let brandResponsiveImage = null;
  let brandWebImage = null;
  let brandTitle = null;
  let brandSubtitle = null;
  let brandDescription = null;
  let brandSections = null;
  let imageGallery = null;

  try {
    const { data } = await getBrandPageById(slug, context.locale);
    contentData = data?.data?.[0];

    brandResponsiveImage = contentData?.content?.brand_responsive_image ?? null;
    brandWebImage = contentData?.content?.brand_web_image ?? null;
    brandTitle = contentData?.content?.brand_title ?? null;
    brandSubtitle = contentData?.content?.brand_subtitle ?? null;
    brandDescription = contentData?.content?.brand_description ?? null;
    brandSections = contentData?.subdocs?.brand_highlighted_products_sections ?? null;
    imageGallery = data?.included?.image_gallery?.[0]?.content?.images ?? null;
  } catch (error) {
    console.error(`Error fetching brand data for slug: "${slug}"`, error.message);
  }

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    category_details: (await import(`../../../prebuild-data/i18n/${context.locale}/category_details.json`)).default,
    categories: (await import(`../../../prebuild-data/i18n/${context.locale}/categories.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      messages,
      brandResponsiveImage,
      brandWebImage,
      brandTitle,
      brandSubtitle,
      brandDescription,
      brand: slug,
      categoriesFromFile,
      brandSections,
      imageGallery,
    },
  };
}
